import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ArrowLeft, Wrench, FileText, Clock, MapPin, Plus, Upload, Download, Trash2, Edit, Calendar, Settings, Camera, Image } from 'lucide-react';
import api from '../lib/api';
import { FilePreview } from '../components/FilePreview';
import { useAuth } from '../contexts/AuthContext';
import { fmtDate } from '../lib/dateUtils';

const DOC_TYPES = [
  { value: 'libretto', label: 'Libretto' },
  { value: 'assicurazione', label: 'Assicurazione' },
  { value: 'verifica_periodica', label: 'Verifica Periodica' },
  { value: 'certificazione', label: 'Certificazione' },
  { value: 'manuale', label: 'Manuale' },
  { value: 'altro', label: 'Altro' },
];

const StatusBadge = ({ status }) => {
  const config = {
    valido: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', label: 'Valido' },
    in_scadenza: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'In Scadenza' },
    scaduto: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Scaduto' },
  };
  const c = config[status] || config.valido;
  return <Badge variant="outline" className={`${c.bg} ${c.text} ${c.border}`}>{c.label}</Badge>;
};

export default function EquipmentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isRole } = useAuth();
  const [equipment, setEquipment] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [history, setHistory] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDocForm, setShowDocForm] = useState(false);
  const [showMaintForm, setShowMaintForm] = useState(false);
  const [showUpload, setShowUpload] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  const [docForm, setDocForm] = useState({
    doc_type: 'altro',
    title: '',
    reference_number: '',
    issuing_entity: '',
    issue_date: '',
    expiry_date: '',
    notes: ''
  });
  
  const [maintForm, setMaintForm] = useState({
    maintenance_date: new Date().toISOString().split('T')[0],
    maintenance_type: 'ordinaria',
    description: '',
    spare_parts: '',
    cost: '',
    supplier: '',
    technician: '',
    next_due_date: ''
  });
  
  const [editForm, setEditForm] = useState({});

  const load = async () => {
    try {
      const [eqRes, docsRes, maintRes, photosRes] = await Promise.all([
        api.get(`/equipment/${id}`),
        api.get(`/equipment-documents?equipment_id=${id}`),
        api.get(`/maintenance?equipment_id=${id}`),
        api.get(`/equipment/${id}/photos`)
      ]);
      setEquipment(eqRes.data);
      setDocuments(docsRes.data);
      setMaintenance(maintRes.data);
      setPhotos(photosRes.data);
      setEditForm({
        name: eqRes.data.name,
        category: eqRes.data.category || '',
        internal_code: eqRes.data.internal_code || '',
        serial_number: eqRes.data.serial_number || '',
        brand: eqRes.data.brand || '',
        model: eqRes.data.model || '',
        purchase_date: eqRes.data.purchase_date || '',
        status: eqRes.data.status || 'disponibile',
        current_location: eqRes.data.current_location || '',
        notes: eqRes.data.notes || '',
        total_hours: eqRes.data.total_hours || 0
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const handleAddDoc = async () => {
    try {
      await api.post('/equipment-documents', { ...docForm, equipment_id: id });
      setShowDocForm(false);
      setDocForm({ doc_type: 'altro', title: '', reference_number: '', issuing_entity: '', issue_date: '', expiry_date: '', notes: '' });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleAddMaint = async () => {
    try {
      await api.post('/maintenance', { 
        ...maintForm, 
        equipment_id: id,
        cost: maintForm.cost ? parseFloat(maintForm.cost) : null
      });
      setShowMaintForm(false);
      setMaintForm({ maintenance_date: new Date().toISOString().split('T')[0], maintenance_type: 'ordinaria', description: '', spare_parts: '', cost: '', supplier: '', technician: '', next_due_date: '' });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleUploadDoc = async (docId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/upload/equipment-doc/${docId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setShowUpload(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore upload');
    }
  };

  const handleDownloadZip = async () => {
    try {
      const res = await api.get('/equipment-documents/zip', { params: { equipment_id: id }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      const cd = res.headers['content-disposition'] || '';
      a.download = cd.split('filename=')[1] || 'documenti.zip';
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || 'Errore scaricamento ZIP'); }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Eliminare questo documento?')) return;
    try {
      await api.delete(`/equipment-documents/${docId}`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleDeleteMaint = async (maintId) => {
    if (!window.confirm('Eliminare questa manutenzione?')) return;
    try {
      await api.delete(`/maintenance/${maintId}`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/equipment/${id}`, editForm);
      setShowEdit(false);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const handleUploadPhoto = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/equipment/${id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore durante il caricamento');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Eliminare questa foto?')) return;
    try {
      await api.delete(`/equipment/${id}/photos/${photoId}`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const canManage = isRole('admin', 'aspp', 'qhse', 'capo_cantiere');

  if (loading) return <div className="p-8 text-center">Caricamento...</div>;
  if (!equipment) return <div className="p-8 text-center text-red-600">Attrezzatura non trovata</div>;

  const statusBadge = (status) => {
    const config = {
      disponibile: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
      assegnata: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
      in_manutenzione: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
      non_disponibile: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' },
      fuori_uso: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
    };
    const c = config[status] || config.disponibile;
    return <Badge variant="outline" className={`${c.bg} ${c.text} ${c.border}`}>{status?.replace('_', ' ')}</Badge>;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="equipment-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/equipment')} className="p-2">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{equipment.name}</h1>
            <p className="text-sm text-slate-500">{equipment.category} - {equipment.internal_code || 'N/D'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {statusBadge(equipment.status)}
          {canManage && (
            <Button variant="outline" onClick={() => setShowEdit(true)}>
              <Edit size={16} className="mr-1" /> Modifica
            </Button>
          )}
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <Wrench size={16} />
              <span className="text-xs">Marca/Modello</span>
            </div>
            <p className="text-sm font-medium">{equipment.brand} {equipment.model || '-'}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <Settings size={16} />
              <span className="text-xs">Seriale</span>
            </div>
            <p className="text-sm font-medium font-mono">{equipment.serial_number || 'N/D'}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <MapPin size={16} />
              <span className="text-xs">Posizione</span>
            </div>
            <p className="text-sm font-medium">{equipment.current_location || 'N/D'}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <Clock size={16} />
              <span className="text-xs">Ore Lavorate</span>
            </div>
            <p className="text-sm font-medium">{equipment.total_hours || 0} ore</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="info">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="info" data-testid="tab-info">Informazioni</TabsTrigger>
          <TabsTrigger value="foto" data-testid="tab-foto">Foto ({photos.length})</TabsTrigger>
          <TabsTrigger value="documenti" data-testid="tab-documenti">Documenti ({documents.length})</TabsTrigger>
          <TabsTrigger value="manutenzioni" data-testid="tab-manutenzioni">Manutenzioni ({maintenance.length})</TabsTrigger>
        </TabsList>

        {/* Info Tab */}
        <TabsContent value="info">
          <Card className="border-slate-200 shadow-sm mt-4">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-slate-500 mb-2">Dati Generali</h3>
                  <dl className="space-y-2">
                    <div className="flex justify-between"><dt className="text-sm text-slate-600">Categoria</dt><dd className="text-sm font-medium">{equipment.category || '-'}</dd></div>
                    <div className="flex justify-between"><dt className="text-sm text-slate-600">Codice Interno</dt><dd className="text-sm font-medium font-mono">{equipment.internal_code || '-'}</dd></div>
                    <div className="flex justify-between"><dt className="text-sm text-slate-600">Numero Seriale</dt><dd className="text-sm font-medium font-mono">{equipment.serial_number || '-'}</dd></div>
                    <div className="flex justify-between"><dt className="text-sm text-slate-600">Data Acquisto</dt><dd className="text-sm font-medium">{equipment.purchase_date ? fmtDate(equipment.purchase_date) : '-'}</dd></div>
                  </dl>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-slate-500 mb-2">Stato e Manutenzione</h3>
                  <dl className="space-y-2">
                    <div className="flex justify-between"><dt className="text-sm text-slate-600">Stato</dt><dd>{statusBadge(equipment.status)}</dd></div>
                    <div className="flex justify-between"><dt className="text-sm text-slate-600">Prossima Manutenzione</dt><dd className="text-sm font-medium">{equipment.next_maintenance_date ? fmtDate(equipment.next_maintenance_date) : '-'}</dd></div>
                    <div className="flex justify-between"><dt className="text-sm text-slate-600">Ore Totali</dt><dd className="text-sm font-medium">{equipment.total_hours || 0} h</dd></div>
                  </dl>
                </div>
              </div>
              {equipment.notes && (
                <div className="mt-4 pt-4 border-t">
                  <h3 className="text-sm font-medium text-slate-500 mb-2">Note</h3>
                  <p className="text-sm text-slate-600">{equipment.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Foto Tab */}
        <TabsContent value="foto">
          <div className="mt-4 space-y-4">
            {canManage && (
              <div className="flex items-center gap-3">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.gif,.webp"
                    className="hidden"
                    onChange={(e) => e.target.files?.[0] && handleUploadPhoto(e.target.files[0])}
                    disabled={uploadingPhoto}
                  />
                  <Button variant="default" disabled={uploadingPhoto} asChild>
                    <span data-testid="upload-photo-btn">
                      <Camera size={16} className="mr-1" />
                      {uploadingPhoto ? 'Caricamento...' : 'Aggiungi Foto'}
                    </span>
                  </Button>
                </label>
                <p className="text-xs text-slate-500">Formati: JPG, PNG, GIF, WebP</p>
              </div>
            )}
            
            {photos.length === 0 ? (
              <Card className="border-slate-200 shadow-sm">
                <CardContent className="flex flex-col items-center justify-center py-12 text-slate-400">
                  <Image size={48} className="mb-3 opacity-50" />
                  <p className="text-sm">Nessuna foto caricata</p>
                  {canManage && <p className="text-xs mt-1">Clicca "Aggiungi Foto" per iniziare</p>}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {photos.map(photo => (
                  <div key={photo.id} className="relative group border rounded-lg overflow-hidden bg-slate-50 aspect-square">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${photo.file_url}`} target="_blank" rel="noopener noreferrer">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${photo.file_url}`}
                        alt={photo.original_name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform"
                      />
                    </a>
                    {canManage && (
                      <Button
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2 p-1 h-auto opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleDeletePhoto(photo.id)}
                        data-testid={`delete-photo-${photo.id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    )}
                    <p className="text-xs text-slate-500 p-2 truncate bg-white/90">{photo.original_name}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Documenti Tab */}
        <TabsContent value="documenti">
          <div className="mt-4 space-y-4">
            <div className="flex flex-wrap gap-2">
              {canManage && (
                <Button onClick={() => setShowDocForm(true)} data-testid="add-doc-btn">
                  <Plus size={16} className="mr-1" /> Nuovo Documento
                </Button>
              )}
              {documents.some(d => d.file_url) && (
                <Button variant="outline" onClick={handleDownloadZip} data-testid="download-docs-zip-btn">
                  <Download size={16} className="mr-1" /> Scarica ZIP
                </Button>
              )}
            </div>
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Documento</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Scadenza</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead>File</TableHead>
                      {canManage && <TableHead className="w-20">Azioni</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documents.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-500">Nessun documento</TableCell></TableRow>
                    ) : documents.map(d => (
                      <TableRow key={d.id}>
                        <TableCell><p className="text-sm font-medium">{d.title}</p></TableCell>
                        <TableCell><Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200 text-xs">{DOC_TYPES.find(t => t.value === d.doc_type)?.label || d.doc_type}</Badge></TableCell>
                        <TableCell className="text-sm">{d.expiry_date ? fmtDate(d.expiry_date) : '-'}</TableCell>
                        <TableCell><StatusBadge status={d.status} /></TableCell>
                        <TableCell>
                          {d.file_url ? (
                            <a href={`${process.env.REACT_APP_BACKEND_URL}${d.file_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-1">
                              <Download size={14} /> Scarica
                            </a>
                          ) : canManage ? (
                            <Button variant="ghost" size="sm" onClick={() => setShowUpload(d.id)} className="text-xs p-1 h-auto">
                              <Upload size={14} className="mr-1" /> Carica
                            </Button>
                          ) : <span className="text-slate-400 text-xs">-</span>}
                        </TableCell>
                        {canManage && (
                          <TableCell>
                            <div className="flex gap-1">
                              {!d.file_url && (
                                <Button variant="ghost" size="sm" onClick={() => setShowUpload(d.id)} className="p-1 h-auto text-blue-600">
                                  <Upload size={14} />
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteDoc(d.id)} className="p-1 h-auto text-red-600">
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Manutenzioni Tab */}
        <TabsContent value="manutenzioni">
          <div className="mt-4 space-y-4">
            {canManage && (
              <Button onClick={() => setShowMaintForm(true)} data-testid="add-maint-btn">
                <Plus size={16} className="mr-1" /> Nuova Manutenzione
              </Button>
            )}
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Descrizione</TableHead>
                      <TableHead>Tecnico</TableHead>
                      <TableHead className="text-right">Costo</TableHead>
                      <TableHead>Prossima</TableHead>
                      {canManage && <TableHead className="w-16">Azioni</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {maintenance.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-500">Nessuna manutenzione</TableCell></TableRow>
                    ) : maintenance.map(m => (
                      <TableRow key={m.id}>
                        <TableCell className="text-sm">{fmtDate(m.maintenance_date)}</TableCell>
                        <TableCell><Badge variant="outline" className={m.maintenance_type === 'straordinaria' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-100 text-slate-600 border-slate-200'}>{m.maintenance_type}</Badge></TableCell>
                        <TableCell className="text-sm text-slate-600 max-w-[200px] truncate">{m.description}</TableCell>
                        <TableCell className="text-sm">{m.technician || '-'}</TableCell>
                        <TableCell className="text-sm text-right font-medium">{m.cost ? `${m.cost.toFixed(2)} EUR` : '-'}</TableCell>
                        <TableCell className="text-sm">{m.next_due_date ? fmtDate(m.next_due_date) : '-'}</TableCell>
                        {canManage && (
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteMaint(m.id)} className="p-1 h-auto text-red-600">
                              <Trash2 size={14} />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Document Dialog */}
      <Dialog open={showDocForm} onOpenChange={setShowDocForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuovo Documento</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5"><Label>Titolo *</Label><Input value={docForm.title} onChange={(e) => setDocForm({ ...docForm, title: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={docForm.doc_type} onValueChange={(v) => setDocForm({ ...docForm, doc_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Riferimento</Label><Input value={docForm.reference_number} onChange={(e) => setDocForm({ ...docForm, reference_number: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Ente Emittente</Label><Input value={docForm.issuing_entity} onChange={(e) => setDocForm({ ...docForm, issuing_entity: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Data Emissione</Label><Input type="date" value={docForm.issue_date} onChange={(e) => setDocForm({ ...docForm, issue_date: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Data Scadenza</Label><Input type="date" value={docForm.expiry_date} onChange={(e) => setDocForm({ ...docForm, expiry_date: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Note</Label><Input value={docForm.notes} onChange={(e) => setDocForm({ ...docForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocForm(false)}>Annulla</Button>
            <Button onClick={handleAddDoc} disabled={!docForm.title}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Maintenance Dialog */}
      <Dialog open={showMaintForm} onOpenChange={setShowMaintForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuova Manutenzione</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Data *</Label><Input type="date" value={maintForm.maintenance_date} onChange={(e) => setMaintForm({ ...maintForm, maintenance_date: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={maintForm.maintenance_type} onValueChange={(v) => setMaintForm({ ...maintForm, maintenance_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ordinaria">Ordinaria</SelectItem>
                  <SelectItem value="straordinaria">Straordinaria</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Descrizione *</Label><Input value={maintForm.description} onChange={(e) => setMaintForm({ ...maintForm, description: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Tecnico</Label><Input value={maintForm.technician} onChange={(e) => setMaintForm({ ...maintForm, technician: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Fornitore</Label><Input value={maintForm.supplier} onChange={(e) => setMaintForm({ ...maintForm, supplier: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Ricambi</Label><Input value={maintForm.spare_parts} onChange={(e) => setMaintForm({ ...maintForm, spare_parts: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Costo (EUR)</Label><Input type="number" step="0.01" value={maintForm.cost} onChange={(e) => setMaintForm({ ...maintForm, cost: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Prossima Manutenzione</Label><Input type="date" value={maintForm.next_due_date} onChange={(e) => setMaintForm({ ...maintForm, next_due_date: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMaintForm(false)}>Annulla</Button>
            <Button onClick={handleAddMaint} disabled={!maintForm.maintenance_date || !maintForm.description}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Equipment Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifica Attrezzatura</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5"><Label>Nome *</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Categoria</Label><Input value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Codice Interno</Label><Input value={editForm.internal_code} onChange={(e) => setEditForm({ ...editForm, internal_code: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Seriale</Label><Input value={editForm.serial_number} onChange={(e) => setEditForm({ ...editForm, serial_number: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Marca</Label><Input value={editForm.brand} onChange={(e) => setEditForm({ ...editForm, brand: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Modello</Label><Input value={editForm.model} onChange={(e) => setEditForm({ ...editForm, model: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Stato</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="disponibile">Disponibile</SelectItem>
                  <SelectItem value="assegnata">Assegnata</SelectItem>
                  <SelectItem value="in_manutenzione">In Manutenzione</SelectItem>
                  <SelectItem value="non_disponibile">Non Disponibile</SelectItem>
                  <SelectItem value="fuori_uso">Fuori Uso</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Ore Lavorate</Label><Input type="number" value={editForm.total_hours} onChange={(e) => setEditForm({ ...editForm, total_hours: parseFloat(e.target.value) || 0 })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Posizione</Label><Input value={editForm.current_location} onChange={(e) => setEditForm({ ...editForm, current_location: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Note</Label><Input value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>Annulla</Button>
            <Button onClick={handleSaveEdit} disabled={!editForm.name}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={!!showUpload} onOpenChange={(o) => { if (!o) { setShowUpload(null); setPendingFile(null); } }}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Carica File</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Input
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.xls,.xlsx"
              onChange={(e) => setPendingFile(e.target.files?.[0] || null)}
              data-testid="upload-file-input"
            />
            <p className="text-xs text-slate-500">Formati: PDF, DOC, DOCX, immagini, Excel</p>
            <FilePreview file={pendingFile} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowUpload(null); setPendingFile(null); }}>Annulla</Button>
            <Button onClick={() => { handleUploadDoc(showUpload, pendingFile); setPendingFile(null); }} disabled={!pendingFile} data-testid="upload-confirm-btn">Carica</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
